import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useEditorRef, useEditorSelector } from 'platejs/react';
import BorderColorIcon from '@mui/icons-material/BorderColor';
// Reuse TextColorMenu's wrap/popover chrome — same dropdown shell as the other
// color controls (mirrors how CellBackgroundMenu borrows it).
import textColorStyles from '../TextColorMenu/TextColorMenu.module.scss';
import toolbarStyles from '../PlateToolbar/PlateToolbar.module.scss';
import { useRecentColors } from '../../hooks/useRecentColors';
import { ColorPickerPanel } from '../ColorPickerPanel/ColorPickerPanel';
import { applyMarkFast } from '../applyMarkFast';

// Highlight is the inline `backgroundColor` mark (FontBackgroundColorPlugin →
// <span style="background-color">), which — unlike the fixed-yellow <mark>
// boolean — takes any color, so it can drive a picker. The raster PDF export's
// box-decoration-break:clone fix targets `mark, span[style*="background-color"]`,
// so highlighted text that wraps across lines survives the export.
//
// Soft, readable tints first (highlight text must stay legible), then brighter
// highlighter tones; the shared panel's custom dialog gives full spectrum.
const PALETTE = [
    '#fbf3db',
    '#fdebd3',
    '#fbdcdc',
    '#f6d8e8',
    '#e7dcf6',
    '#d8e6fb',
    '#d6eef7',
    '#d8eedd',
    '#efefef',
    '#ffffff',
    '#ffe14d',
    '#ffc04d',
    '#ff9a4d',
    '#ff6b6b',
    '#ff66c4',
    '#b06bff',
    '#6b8cff',
    '#4dc3ff',
    '#4ddb8a',
    '#b3b3b3',
];

export function HighlightColorMenu() {
    const editor = useEditorRef();
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);
    const [recents, pushRecent] = useRecentColors('plateEditor.recentColors.highlight');

    const currentColor: string | undefined = useEditorSelector((ed: any) => ed?.api?.marks?.()?.backgroundColor, []);

    useEffect(() => {
        if (!open) return;
        function handleClickOutside(e: any) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        }
        function handleEsc(e: any) {
            if (e.key === 'Escape') setOpen(false);
        }
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEsc);
        };
    }, [open]);

    const applyColor = (hex: string) => {
        if (!hex) return;
        const ed: any = editor;
        applyMarkFast(ed, 'backgroundColor', hex, (v) => ed?.tf?.backgroundColor?.addMark?.(v));
        setOpen(false);
    };

    // "No highlight" clears both the colored backgroundColor span and any
    // legacy fixed-yellow <mark> (highlight) so either highlight form is removed.
    const clearColor = () => {
        const ed: any = editor;
        ed?.tf?.removeMarks?.('backgroundColor');
        ed?.tf?.removeMarks?.('highlight');
        setOpen(false);
    };

    const swatchColor = currentColor || '#fbf3db';

    return (
        <div className={textColorStyles.wrap} ref={wrapRef}>
            <button
                type="button"
                className={clsx(toolbarStyles.btn, open && toolbarStyles.btnActive)}
                onMouseDown={(e) => {
                    e.preventDefault();
                    setOpen((o) => !o);
                }}
                title="Highlight color"
                aria-label="Highlight color"
                aria-haspopup="true"
                aria-expanded={open}
            >
                {/* Glyph stays the toolbar default (black); only the underline
                    bar reflects the current highlight color — the highlighter
                    swatch convention from Google Docs / Word. */}
                <span style={{ position: 'relative', display: 'inline-flex', lineHeight: 0 }}>
                    <BorderColorIcon fontSize="small" />
                    <span
                        aria-hidden
                        style={{
                            position: 'absolute',
                            left: 1,
                            right: 1,
                            bottom: 0,
                            height: 3,

                            backgroundColor: swatchColor,
                        }}
                    />
                </span>
            </button>
            {open && (
                <div
                    className={textColorStyles.popover}
                    role="menu"
                    aria-label="Pick highlight color"
                    onMouseDown={(e) => e.preventDefault()}
                >
                    <ColorPickerPanel
                        value={currentColor}
                        previewColor={swatchColor}
                        palette={PALETTE}
                        recents={recents}
                        onPick={applyColor}
                        pushRecent={pushRecent}
                        onClear={clearColor}
                        clearLabel="No highlight"
                    />
                </div>
            )}
        </div>
    );
}
